import React, { useState, useCallback, useRef, useEffect } from "react";
import { GoogleMap, useLoadScript, Autocomplete } from "@react-google-maps/api";

// Libraries for Google Maps API
const libraries = ["places", "marker"];

// Default to Islamabad, Pakistan if no location is provided
const DEFAULT_LOCATION = {
    lat: 33.6844, // Latitude for Islamabad
    lng: 73.0479, // Longitude for Islamabad
    pinLocationName: "Islamabad, Pakistan",
};

const GoogleMapCustom = ({
    defaultLat,
    defaultLng,
    onLocationChange,
    isReinit,
    pinLocationName: initialPinLocationName,
}) => {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: "AIzaSyAE6MQgx8MwLZCVOp7Qb11j4zG6hd9YikM",
        libraries,
        version: "beta",
    });

    // Use provided location or fallback to Islamabad
    const [markerPosition, setMarkerPosition] = useState({
        lat: defaultLat || DEFAULT_LOCATION.lat,
        lng: defaultLng || DEFAULT_LOCATION.lng,
        pinLocationName: initialPinLocationName || DEFAULT_LOCATION.pinLocationName,
    });

    const [inputValue, setInputValue] = useState(initialPinLocationName || DEFAULT_LOCATION.pinLocationName);
    const [mapCenter, setMapCenter] = useState({ lat: markerPosition.lat, lng: markerPosition.lng });
    const autocompleteRef = useRef(null);
    const markerRef = useRef(null);

    // Sync markerPosition changes with parent
    useEffect(() => {
        if (onLocationChange) {
            onLocationChange(markerPosition);
        }
    }, [markerPosition, onLocationChange]);

    // Update marker position when dragged
    const handleMarkerDragEnd = useCallback((event) => {
        const newLat = event.latLng.lat();
        const newLng = event.latLng.lng();

        // Reverse geocoding to get location name
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
            if (status === "OK" && results[0]) {
                const newPosition = {
                    lat: newLat,
                    lng: newLng,
                    pinLocationName: results[0].formatted_address,
                };
                setMarkerPosition(newPosition);
                setInputValue(results[0].formatted_address || "");
            } else {
                console.error("Geocoding failed:", status);
            }
        });
    }, []);

    // Handle location selection via Autocomplete
    const handlePlaceChanged = () => {
        const place = autocompleteRef.current.getPlace();
        if (place.geometry) {
            const newCenter = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
                pinLocationName: place.formatted_address || place.name,
            };
            setMapCenter(newCenter);
            setMarkerPosition(newCenter);
            setInputValue(newCenter.pinLocationName || "");
        }
    };

    // Handle manual input change
    const handleInputChange = (event) => {
        setInputValue(event.target.value);
        setMarkerPosition((prev) => ({
            ...prev,
            pinLocationName: event.target.value,
        }));
    };

    // Initialize marker when map is loaded
    const onMapLoad = useCallback((map) => {
        const marker = new window.google.maps.marker.AdvancedMarkerElement({
            position: markerPosition,
            map: map,
            gmpDraggable: true,
        });

        marker.addListener("dragend", (event) => {
            handleMarkerDragEnd(event);
        });

        markerRef.current = marker;
    }, [markerPosition, handleMarkerDragEnd]);

    // Update marker position when markerPosition changes
    useEffect(() => {
        if (markerRef.current) {
            markerRef.current.position = markerPosition;
        }
    }, [markerPosition]);

    if (!isLoaded) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <Autocomplete onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)} onPlaceChanged={handlePlaceChanged}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder="Search for a location"
                    style={{
                        width: "100%",
                        padding: "10px",
                        marginBottom: "10px",
                        fontSize: "16px",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                    }}
                />
            </Autocomplete>

            <GoogleMap
                mapContainerStyle={{ width: "100%", height: "400px" }}
                center={mapCenter}
                zoom={14}
                onLoad={onMapLoad}
            />
        </div>
    );
};

export default GoogleMapCustom;
